import { Card, Typography } from '@mui/material';
import { ArrayField, AutocompleteArrayInput, ChipField, Datagrid, DateField, Filter, FunctionField, NumberField, ReferenceArrayField, ReferenceArrayInput, ReferenceField, ReferenceInput, SelectArrayInput, SelectField, SelectInput, SingleFieldList, TextField, useListContext, usePermissions, useRecordContext, useTranslate,
  AutocompleteInput,} from 'react-admin';
import { PERMISSIONS } from '../../constants';
import ReportList from '../../components/ReportList';
import dayjs from 'dayjs';
import { BetweenDatesInput } from '../../components/BetweenDatesInput';
import ListPagination from "../../components/ListPagination";
import { Permission } from '../../components';
import { RatingPollItemType } from '../../models';
import { getChainId } from '../../lib';
import { formatAttendantLabel, filterToQuery } from '../../utils';

const titleSx = {
  margin: '10px 0 10px 0',
  textAlign: 'center',
  fontWeight: 'bold'
};

const FilterTablePrint = () => {
  const { filterValues } = useListContext();
  const { permissions } = usePermissions();
  return (
    <Datagrid bulkActionButtons={false} data={[{ ...filterValues, generatedReportAt: dayjs().toISOString() }]} total={1}>
      <DateField source="from" textAlign="center" label="De" sortable={false} locales="pt-BR" />
      <DateField source="to" textAlign="center" label="Até" sortable={false} locales="pt-BR" />
      <DateField source="generatedReportAt" textAlign="center" label="Gerado às" showTime sortable={false} locales="pt-BR" />
      <SelectField
        source="pollType"
        label="Tipo de Enquete"
        emptyText="Todos"
        choices={RatingPollItemType.ratingPollItemTypeChoices}
      />
      <ReferenceArrayField source="stationIds" emptyText="Todos" reference="stations" label="Postos" sortable={false}>
        <SingleFieldList>
          <ChipField source="name" />
        </SingleFieldList>
      </ReferenceArrayField>
      <ReferenceArrayField source="attendantIds" emptyText="Todos" reference="attendants" label="Frentistas" sortable={false}>
        <SingleFieldList>
          <ChipField source="name" />
        </SingleFieldList>
      </ReferenceArrayField>
      <ReferenceArrayField source="fuelIds" emptyText="Todos" reference="fuels" label="Combustíveis" sortable={false}>
        <SingleFieldList>
          <ChipField source="name" />
        </SingleFieldList>
      </ReferenceArrayField>
      {
        permissions && permissions.includes(PERMISSIONS.ADMIN) &&
        <ReferenceField source="chainId" emptyText="Todos" reference="chains" label="Rede" sortable={false}>
          <TextField source="name" />
        </ReferenceField>
      }
    </Datagrid>
  );
};

const ListFilters = () => {
  const translate = useTranslate();

  return (
    <Filter>
      <BetweenDatesInput alwaysOn />
      <SelectInput
        source="pollType"
        label="Tipo de Enquete"
        choices={RatingPollItemType.ratingPollItemTypeChoices}
        emptyText="Todos"
        alwaysOn
      >

      </SelectInput>
      <ReferenceArrayInput
        reference="attendants"
        source="attendantIds"
        perPage={null}
        sort={{ field: "name", order: "ASC" }}
        alwaysOn
      >
        <SelectArrayInput
          optionText={record => formatAttendantLabel(record)}
          label="Frentista"
          style={{ minWidth: 180 }}
        />
      </ReferenceArrayInput>
      {
        Permission({
          permission: PERMISSIONS.ADMIN,
          children: (
            <ReferenceInput
              source="chainId"
              reference="chains"
              perPage={false}
              sort={{ field: 'name', order: 'ASC' }}
              alwaysOn
            >
              <AutocompleteInput filterToQuery={filterToQuery} label="Rede" optionText="name" />
            </ReferenceInput>
          )
        })
      }
      <ReferenceArrayInput
        source="stationIds"
        reference="stations"
        perPage={null}
        sort={{ field: "name", order: "ASC" }}
        alwaysOn
      >
        <AutocompleteArrayInput
          filterToQuery={filterToQuery}
          label="Posto"
          optionText="name"
          sx={{ minWidth: 180, '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': { minWidth: 50 } }}
          noOptionsText={translate('ra.input.autocomplete.no_options_text')}
        />
      </ReferenceArrayInput>
      <ReferenceArrayInput
        source="fuelIds"
        reference="fuels"
        perPage={null}
        sort={{ field: "name", order: "ASC" }}
        alwaysOn
      >
        <SelectArrayInput
          optionText={"name"}
          style={{ minWidth: 180 }}
          label="Combustíveis"
        />
      </ReferenceArrayInput>
    </Filter>
  )
}

const FieldsRatting = [
  <FunctionField
    label="Frentista"
    render={(record) => record && record.attendantid !== "0" ?
      <ReferenceField
        source="attendantid"
        reference="attendants"
        textAlign="center"
        emptyText="Não identificado"
        link={false}
        sortable={false}
      >
        <FunctionField render={record => formatAttendantLabel(record)} />
      </ReferenceField>
      : <TextField source="name" emptyText="Todos" />
    }
  />,
  <NumberField
    source="total"
    label="Quantidade de usos"
    textAlign="center"
    emptyText="0"
    sortable={false}
  />,
  <NumberField
    source="avg_station"
    label="Média de nota do posto"
    textAlign="center"
    emptyText="0"
    options={{
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }}
    sortable={false}
  />,
  <NumberField
    source="avg_attendant"
    label="Média de nota dos frentistas"
    textAlign="center"
    emptyText="0"
    options={{
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }}
    sortable={false}
  />
];

const ExpandPanel = () => {
  const record = useRecordContext();
  if (!record) return null;

  return (
    <Card sx={{ p: 2 }}>
      <Typography sx={titleSx}>Detalhamento</Typography>
      <Datagrid bulkActionButtons={false} data={record.expandedAttendants} total={1}>
        {FieldsRatting}
      </Datagrid>
    </Card>
  );
};

const ListRatingPollGroup = () => {
  const props = useListContext();
  let data = [];

  if (props.data) {
    data = props.data.map((item) => {
      if (item.attendants.length > 1) {
        const expandedAttendants = item.attendants.slice(1).map((attendant) => {
          return ({
            ...attendant,
            id: attendant.attendantid
          })
        })
        return { ...item, attendants: [{ id: `total-${item.id}`, ...item.attendants[0], isExpand: true, expandedAttendants }] }
      } else {
        return { ...item, isExpand: false }
      }
    });
  }

  return (
    <>
      <Typography sx={titleSx}>Enquetes</Typography>
      <Datagrid bulkActionButtons={false} data={data} total={1}>
        <FunctionField
          label="Enquete"
          render={(record) => record && record.id !== "0" ?
            <ReferenceField
              source="id"
              reference="rating-poll-items"
              textAlign="center"
              link={false}
              sortable={false}
            >
              <TextField source="name" />
            </ReferenceField>
            : <TextField source="name" emptyText="Todos" />
          }
        />
        <ArrayField source="attendants" label="Frentistas" sortable={false}>
          <Datagrid bulkActionButtons={false} expand={ExpandPanel} isRowExpandable={(row) => row.isExpand} >
            {FieldsRatting}
          </Datagrid>
        </ArrayField>
      </Datagrid>
    </>
  );
};

const ReportRatingPollGroup = (props) => {
  return (
    <Permission permission={PERMISSIONS.RATING_POLL_ITEMS_REPORT}>
      <ReportList
        {...props}
        basePath="report-rating-poll-group"
        title="Enquetes"
        resource="chains/chainId/reports/rating-poll-group"
        filters={<ListFilters />}
        filterTablePrint={<FilterTablePrint />}
        filterDefaultValues={{
          from: dayjs().startOf('day').subtract(1, "month").toISOString(),
          to: dayjs().endOf('day').toISOString(),
          chainId: getChainId(),
        }}
        pagination={<ListPagination />}
        perPage={25}
        isReport={false}
      >
        <ListRatingPollGroup />
      </ReportList>
    </Permission>
  );
};

export default ReportRatingPollGroup;